// src/pages/IpdReports.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import AppConst from "../apis/appconst";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import Select from "react-select";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";

// ---------- small helpers ----------
const pad = (n) => String(n).padStart(2, "0");
const getTodayYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const getNowHHMM = () => {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
function parseServerDateToLocal(s) {
  if (!s) return null;
  if (s instanceof Date) return s;
  const normalized = String(s).trim().replace(" ", "T");
  const m = normalized.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (!m) {
    const fallback = new Date(s);
    return isNaN(fallback) ? null : fallback;
  }
  const year = Number(m[1]), month = Number(m[2]) - 1, day = Number(m[3]);
  const hour = Number(m[4] ?? 0), minute = Number(m[5] ?? 0), second = Number(m[6] ?? 0);
  return new Date(year, month, day, hour, minute, second);
}
function formatDateTime12(s) {
  const dt = parseServerDateToLocal(s);
  if (!dt) return "";
  const day = pad(dt.getDate());
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const mon = months[dt.getMonth()];
  const year = dt.getFullYear();
  let hr = dt.getHours();
  const min = pad(dt.getMinutes());
  hr = hr % 12 || 12;
  const ampm = dt.getHours() >= 12 ? "PM" : "AM";
  return `${day}-${mon}-${year} ${pad(hr)}:${min} ${ampm}`;
}

// ---------- component ----------
export default function IpdReports() {
  // filters
  const [filters, setFilters] = useState({
    dischargeFrom: getTodayYMD(),
    dischargeFromTime: "00:00",
    dischargeTo: getTodayYMD(),
    dischargeToTime: getNowHHMM(),
    doctorId: "",
    doctorSearch: "",
    brokerId: "",
    brokerSearch: "",
    search: "",
    status: "1"
  });

  const [records, setRecords] = useState([]);
  const [docsFromServer, setDocsFromServer] = useState([]);
  const [brokersFromServer, setBrokersFromServer] = useState([]);
  const [brokerOptions, setBrokerOptions] = useState([]);
  const [loadingBrokers, setLoadingBrokers] = useState(false);
  const [totals, setTotals] = useState({
    totalIpdBill: "0.00", paidCash: "0.00", paidCard: "0.00", paidOnline: "0.00", totalDue: "0.00", totalPatients: 0, totalCommission: "0.00", totalBrokerCommission: "0.00"
  });

  // doctor pay
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payRow, setPayRow] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payProcessing, setPayProcessing] = useState(false);
  const [payError, setPayError] = useState("");

  // broker pay
  const [brokerPayModalOpen, setBrokerPayModalOpen] = useState(false);
  const [brokerPayRow, setBrokerPayRow] = useState(null);
  const [brokerPayAmount, setBrokerPayAmount] = useState("");
  const [brokerPayProcessing, setBrokerPayProcessing] = useState(false);
  const [brokerPayError, setBrokerPayError] = useState("");

  // ---------- utils for commissions ----------
  const getCommissionForRow = (r) => {
    if (!r) return 0;
    const candidates = [ r.ipd_bill_commission, r.ipd_bill_commission_doctor, r.ipd_commission, r.commission, r.doctor_commission ];
    for (const c of candidates) {
      if (typeof c !== 'undefined' && c !== null && c !== '') {
        const n = Number(c);
        if (!isNaN(n)) return n;
        const parsed = parseFloat(String(c).replace(/[^0-9.-]/g, ""));
        if (!isNaN(parsed)) return parsed;
      }
    }
    return 0;
  };
  const getDoctorNumbers = (r) => {
    const paidCandidates = [ r.ipd_commission_paid, r.ipd_bill_commission_paid, r.doctor_commission_paid, r.ipd_doctor_commission_paid, r.commission_paid ];
    let paid = 0;
    for (const p of paidCandidates) {
      if (typeof p !== 'undefined' && p !== null && p !== '') {
        const n = Number(p);
        if (!isNaN(n)) { paid = n; break; }
      }
    }
    const commission = getCommissionForRow(r);
    const due = Math.max(commission - paid, 0);
    return { commission: Number(commission || 0), paid: Number(paid || 0), due: Number(due || 0) };
  };

  // broker numbers
  const getBrokerNumbers = (r) => {
    if (!r) return { commission: 0, paid: 0, due: 0 };
    const commissionCandidates = [ r.ipd_bill_broker_commission, r.ipd_bill_broker_commission_amount, r.broker_commission, r.ipd_bill_broker_comm ];
    let commission = 0;
    for (const c of commissionCandidates) {
      if (typeof c !== 'undefined' && c !== null && c !== '') {
        const n = Number(c);
        if (!isNaN(n)) { commission = n; break; }
        const parsed = parseFloat(String(c).replace(/[^0-9.-]/g, ""));
        if (!isNaN(parsed)) { commission = parsed; break; }
      }
    }
    const paidCandidates = [ r.ipd_bill_broker_commission_paid, r.ipd_broker_commission_paid, r.broker_commission_paid, r.ipd_broker_paid ];
    let paid = 0;
    for (const p of paidCandidates) {
      if (typeof p !== 'undefined' && p !== null && p !== '') {
        const n = Number(p);
        if (!isNaN(n)) { paid = n; break; }
      }
    }
    const due = Math.max(commission - paid, 0);
    return { commission: Number(commission || 0), paid: Number(paid || 0), due: Number(due || 0) };
  };

  const getBrokerNameForRow = (r) => {
    if (!r) return "";
    if (r.ipd_bill_broker_name && String(r.ipd_bill_broker_name).trim() !== "") return String(r.ipd_bill_broker_name);
    if (r.ipd_bill_broker_id) {
      const b = brokersFromServer.find(x => String(x.user_id) === String(r.ipd_bill_broker_id) || String(x.id) === String(r.ipd_bill_broker_id));
      if (b) return b.fname || b.name || b.username || String(r.ipd_bill_broker_id);
      return String(r.ipd_bill_broker_id);
    }
    return r.broker_name || r.broker || "";
  };

  // ---------- loaders ----------
  const loadRecords = async () => {
    try {
      const fd = new FormData();
      fd.append("page_no", "none");
      if (filters.dischargeFrom) fd.append("dischargeFrom", filters.dischargeFrom);
      if (filters.dischargeFromTime) fd.append("dischargeFromTime", filters.dischargeFromTime);
      if (filters.dischargeTo) fd.append("dischargeTo", filters.dischargeTo);
      if (filters.dischargeToTime) fd.append("dischargeToTime", filters.dischargeToTime);
      if (typeof filters.status !== "undefined" && filters.status !== "") fd.append("status", String(filters.status));
      if (filters.search) fd.append("search_key", filters.search);
      if (filters.doctorId) fd.append("doctor_id", filters.doctorId);
      if (filters.brokerId) fd.append("broker_id", filters.brokerId);

      const url = AppConst.BASE_URL + (AppConst.getIpdDischargedUrl4 || AppConst.getIpdListUrl2 || "/api/get_ipd_discharged");
      const res = await axios.post(url, fd);
      if (res?.data?.status && Array.isArray(res.data.records)) {
        setRecords(res.data.records);
        // if server returns aggregated totals
        const t = res.data.totals || {};
        setTotals(prev => ({
          ...prev,
          totalIpdBill: (t.sum_ipd_bill ?? 0).toFixed ? (t.sum_ipd_bill ?? 0).toFixed(2) : Number(t.sum_ipd_bill ?? 0).toFixed(2),
          paidCash: (t.total_cash ?? 0).toFixed(2),
          paidCard: (t.total_card ?? 0).toFixed(2),
          paidOnline: (t.total_online ?? 0).toFixed(2),
          totalDue: (t.sum_due ?? 0).toFixed(2),
          totalPatients: (t.count ?? (res.data.records || []).length),
          totalCommission: (t.total_commission ?? 0).toFixed ? (t.total_commission ?? 0).toFixed(2) : Number(t.total_commission ?? 0).toFixed(2),
          totalBrokerCommission: (t.total_broker_commission ?? 0).toFixed ? (t.total_broker_commission ?? 0).toFixed(2) : Number(t.total_broker_commission ?? 0).toFixed(2)
        }));
      } else {
        setRecords([]);
      }
    } catch (err) {
      console.error("loadRecords err", err);
      setRecords([]);
    }
  };

  const fetchDoctors = async () => {
    try {
      const fd = new FormData();
      fd.append("type", "doctor");
      fd.append("page_no", "none");
      const res = await axios.post(AppConst.BASE_URL + AppConst.getUserListUrl, fd);
      if (res?.data?.status && Array.isArray(res.data.records)) setDocsFromServer(res.data.records);
    } catch (e) {
      console.error("fetchDoctors", e);
    }
  };

  const fetchBrokers = async (search = "") => {
    try {
      setLoadingBrokers(true);
      const fd = new FormData();
      fd.append("search_key", search || "");
      fd.append("page_no", "none");
      const res = await axios.post(AppConst.BASE_URL + (AppConst.getBrokerListUrl || "/api/get_broker_list"), fd);
      const records = res?.data?.records && Array.isArray(res.data.records) ? res.data.records : [];
      setBrokersFromServer(records);
      const opts = records.map(r => ({ value: String(r.broker_id ?? r.id ?? r.user_id ?? ""), label: `${(r.fname||"").trim()} ${(r.lname||"").trim()}`.trim() || (r.mobile||"Unknown"), raw: r }));
      setBrokerOptions(prev => {
        if (filters.brokerId) {
          const exists = opts.find(o => String(o.value) === String(filters.brokerId));
          if (!exists) return [{ value: String(filters.brokerId), label: filters.brokerSearch || String(filters.brokerId), raw: null }, ...opts];
        }
        return opts;
      });
    } catch (err) {
      console.error("fetchBrokers", err);
      setBrokersFromServer([]);
      setBrokerOptions([]);
    } finally {
      setLoadingBrokers(false);
    }
  };

  useEffect(() => {
    loadRecords();
    fetchDoctors();
    fetchBrokers();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const t = setTimeout(() => loadRecords(), 220);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [filters]);

  // ---------- filteredRecords (client-side filter fallback) ----------
  const filteredRecords = useMemo(() => {
    let data = [...records];
    data = data.filter(r => String(r.status) === String(filters.status));
    // broker filter
    if (filters.brokerId && filters.brokerId !== "") {
      const sel = String(filters.brokerId).toLowerCase();
      data = data.filter(r => {
        const idMatch = String(r.ipd_bill_broker_id ?? r.broker_id ?? '').toLowerCase() === sel;
        const nameMatch = (r.ipd_bill_broker_name || r.broker_name || '').toLowerCase().includes(sel);
        return idMatch || nameMatch;
      });
    }
    // doctor filter
    if (filters.doctorId && filters.doctorId !== "") {
      const sel = String(filters.doctorId).toLowerCase();
      data = data.filter(r => {
        const idMatch = String(r.ipd_bill_refered_doctor ?? r.ipd_refer_doctor ?? r.ipd_ref_doc ?? r.ipd_cons_doc ?? '').toLowerCase() === sel;
        const nameMatch = (r.ipd_bill_refered_doctor_name || r.ipd_refer_doctor_name || r.consultant_name || '').toLowerCase().includes(sel);
        return idMatch || nameMatch;
      });
    }
    // search
    if (filters.search && filters.search.trim() !== "") {
      const skRaw = filters.search.trim();
      const sk = skRaw.toLowerCase();
      const digitsOnly = skRaw.replace(/\D/g, '');
      data = data.filter(r => {
        const uhid = ((r.ipd_remarks==="Moved from OPD"?r.opd_uhid_move:r.ipd_uhid) || '').toString().toLowerCase();
        const name = (r.ipd_patient_name || '').toString().toLowerCase();
        const phones = [r.ipd_patient_mobile, r.ipd_cont_number, r.ipd_patient_phone, r.patient_mobile, r.mobile, r.phone].filter(Boolean).map(p => String(p).toLowerCase());
        const ipdNoDisplayed = `000${r.ipd_id ?? r.id ?? ''}`.toLowerCase();
        if (name.includes(sk)) return true;
        if (uhid.includes(sk)) return true;
        if (ipdNoDisplayed.includes(sk)) return true;
        for (const ph of phones) {
          if (ph.includes(sk)) return true;
          if (digitsOnly && ph.replace(/\D/g, '').includes(digitsOnly)) return true;
        }
        if (digitsOnly) {
          const ipdIdStr = String(r.ipd_id ?? r.id ?? '').replace(/\D/g, '');
          if (ipdIdStr.includes(digitsOnly)) return true;
        }
        return false;
      });
    }
    // date filter
    if (filters.dischargeFrom || filters.dischargeTo) {
      const fromDt = filters.dischargeFrom ? new Date(`${filters.dischargeFrom}T${filters.dischargeFromTime || '00:00'}`) : null;
      const toDt = filters.dischargeTo ? new Date(`${filters.dischargeTo}T${filters.dischargeToTime || '23:59'}`) : null;
      data = data.filter(r => {
        const ds = r.ipd_discharge_date || r.discharge_datetime || r.discharge_date || null;
        const dt = parseServerDateToLocal(ds);
        if (!dt) return false;
        if (fromDt && dt < fromDt) return false;
        if (toDt && dt > toDt) return false;
        return true;
      });
    }
    return data;
  }, [records, filters, docsFromServer, brokersFromServer]);

  // ---------- totals computed client-side ----------
  useEffect(() => {
    const num = v => { const n = Number(v ?? 0); return isNaN(n) ? 0 : n; };
    let totalIpd = 0, totalAdvance = 0, paidCash = 0, paidCard = 0, paidOnline = 0, totalDue = 0, totalCommission = 0, totalBrokerCommission = 0;
    filteredRecords.forEach(r => {
      const advance = num(r.ipd_advance ?? r.advance ?? r.advance_amt ?? 0);
      const ipdBill = num(r.ipd_bill_total_final ?? r.ipd_ipd_bill_amount ?? r.ipd_bill_amount ?? r.ipd_final_amt ?? 0);
      const due = num(r.ipd_bill_total_due ?? r.ipd_due ?? r.due ?? 0);
      const comm = getCommissionForRow(r);
      const bcomm = getBrokerNumbers(r).commission;
      totalAdvance += advance;
      totalIpd += ipdBill;
      totalDue += due;
      totalCommission += Number(comm ?? 0);
      totalBrokerCommission += Number(bcomm ?? 0);
      paidCash += num(r.ipd_paid_cash ?? r.paid_cash ?? r.paidCash ?? 0);
      paidCard += num(r.ipd_paid_card ?? r.paid_card ?? r.paidCard ?? 0);
      paidOnline += num(r.ipd_paid_online ?? r.paid_online ?? r.paidOnline ?? 0);
    });
    setTotals(prev => ({ ...prev, totalIpdBill: totalIpd.toFixed(2), totalAdvance: totalAdvance.toFixed(2), paidCash: paidCash.toFixed(2), paidCard: paidCard.toFixed(2), paidOnline: paidOnline.toFixed(2), totalDue: totalDue.toFixed(2), totalPatients: filteredRecords.length, totalCommission: totalCommission.toFixed(2), totalBrokerCommission: totalBrokerCommission.toFixed(2) }));
  }, [filteredRecords]);

  // ---------- handlers ----------
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // doctor pay modal handlers
  const openPayModal = (row) => {
    if (!row) return;
    const { due } = getDoctorNumbers(row);
    setPayRow(row); setPayAmount(due > 0 ? due.toFixed(2) : ""); setPayError(""); setPayModalOpen(true);
  };
  const closePayModal = () => { setPayRow(null); setPayAmount(""); setPayError(""); setPayModalOpen(false); };

  const handlePaySubmit = async (e) => {
    e.preventDefault();
    if (!payRow) return;
    const { commission, paid, due } = getDoctorNumbers(payRow);
    const amt = parseFloat(payAmount || 0);
    if (isNaN(amt) || amt <= 0) { setPayError("Please enter a valid amount."); return; }
    if (amt > due + 0.01) { setPayError("Amount cannot be more than due."); return; }
    try {
      setPayProcessing(true); setPayError("");
      const fd = new FormData();
      if (payRow.ipd_id) fd.append("ipd_id", String(payRow.ipd_id));
      else if (payRow.id) fd.append("ipd_id", String(payRow.id));
      if (payRow.ipd_uhid) fd.append("ipd_uhid", String(payRow.ipd_uhid));
      fd.append("pay_amount", amt.toFixed(2));
      fd.append("note", `IPD doctor commission payment via UI`);
      const res = await axios.post(AppConst.BASE_URL + (AppConst.payIpdDoctorCommissionUrl || "/api/pay_ipd_doctor_commission"), fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data?.status) { Swal.fire("Saved", res.data.message || "Payment saved", "success"); closePayModal(); loadRecords(); }
      else setPayError(res.data?.message || "Failed to save payment");
    } catch (err) {
      console.error("pay doctor err", err);
      setPayError(err.response?.data?.message || err.message || "Failed to save payment");
    } finally { setPayProcessing(false); }
  };

  // broker pay modal handlers
  const openBrokerPayModal = (row) => {
    if (!row) return;
    const { due } = getBrokerNumbers(row);
    setBrokerPayRow(row); setBrokerPayAmount(due > 0 ? due.toFixed(2) : ""); setBrokerPayError(""); setBrokerPayModalOpen(true);
  };
  const closeBrokerPayModal = () => { setBrokerPayRow(null); setBrokerPayAmount(""); setBrokerPayError(""); setBrokerPayModalOpen(false); };

  const handleBrokerPaySubmit = async (e) => {
    e.preventDefault();
    if (!brokerPayRow) return;
    const { commission, paid, due } = getBrokerNumbers(brokerPayRow);
    const amt = parseFloat(brokerPayAmount || 0);
    if (isNaN(amt) || amt <= 0) { setBrokerPayError("Please enter a valid amount."); return; }
    if (amt > due + 0.01) { setBrokerPayError("Amount cannot be more than due."); return; }
    try {
      setBrokerPayProcessing(true); setBrokerPayError("");
      const fd = new FormData();
      if (brokerPayRow.ipd_id) fd.append("ipd_id", String(brokerPayRow.ipd_id));
      else if (brokerPayRow.id) fd.append("ipd_id", String(brokerPayRow.id));
      const brokerIdToSend = brokerPayRow.ipd_bill_broker_id ?? brokerPayRow.broker_id ?? brokerPayRow.broker_user_id;
      if (brokerIdToSend) fd.append("broker_id", String(brokerIdToSend));
      fd.append("pay_amount", amt.toFixed(2));
      fd.append("note", `IPD broker commission payment via UI`);
      const endpoint = AppConst.BASE_URL + (AppConst.payIpdBrokerCommissionUrl || "/api/pay_ipd_broker_commission");
      const res = await axios.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data?.status) { Swal.fire("Saved", res.data.message || "Broker payment saved", "success"); closeBrokerPayModal(); loadRecords(); }
      else setBrokerPayError(res.data?.message || "Failed to save broker payment");
    } catch (err) {
      console.error("pay broker err", err);
      setBrokerPayError(err.response?.data?.message || err.message || "Failed to save payment");
    } finally { setBrokerPayProcessing(false); }
  };

  // export PDF (keeps doc & broker commissions)
  const handleExportPdf = async () => {
    try {
      const rows = filteredRecords;
      if (!rows || rows.length === 0) { Swal.fire('Nothing to export','No records available to export.','info'); return; }
      const doc = new jsPDF('landscape', 'pt', 'a4');
      const title = `IPD Discharged Report`;
      const subtitle = `Date: ${getTodayYMD()}   Broker: ${filters.brokerSearch || 'All'}`;
      doc.setFontSize(14); doc.text(title, 40, 40); doc.setFontSize(10); doc.text(subtitle, 40, 60);
      const summaryY = 78; doc.setFontSize(9);
      doc.text(`Total Patients: ${totals.totalPatients}   Total IPD Bill: ₹${totals.totalIpdBill}     Broker Commission: ₹${totals.totalBrokerCommission}`, 40, summaryY);
      const head = [['SL','IPD No','UHID','Patient Name','Discharge Date','Doc Comm','Doc Paid','Doc Due','Broker','Broker Comm','Broker Paid','Broker Due','IPD Bill','Cash','Card','Online','Due']];
      const body = rows.map((r, idx) => {
        const ipdNo = `000${r.ipd_id ?? r.id ?? ''}`;
        const uhid = (r.ipd_remarks === "Moved from OPD" ? r.opd_uhid_move : r.ipd_uhid) || '';
        const name = (r.ipd_patient_name || '').replace(/\s+/g,' ');
        const discharge = formatDateTime12(r.ipd_discharge_date || r.discharge_datetime || r.discharge_date || '');
        const docNums = getDoctorNumbers(r);
        const brokerNums = getBrokerNumbers(r);
        const ipdBill = Number(r.ipd_bill_total_final ?? r.ipd_ipd_bill_amount ?? r.ipd_bill_amount ?? 0).toFixed(2);
        const cash = Number(r.ipd_paid_cash ?? 0).toFixed(2);
        const card = Number(r.ipd_paid_card ?? 0).toFixed(2);
        const online = Number(r.ipd_paid_online ?? 0).toFixed(2);
        const dueBill = Number(r.ipd_bill_total_due ?? r.ipd_due ?? r.due ?? 0).toFixed(2);
        return [ String(idx+1), ipdNo, uhid, name, discharge, docNums.commission.toFixed(2), docNums.paid.toFixed(2), docNums.due.toFixed(2), getBrokerNameForRow(r), brokerNums.commission.toFixed(2), brokerNums.paid.toFixed(2), brokerNums.due.toFixed(2), ipdBill, cash, card, online, dueBill ];
      });
      autoTable(doc, {
        startY: 95, head, body,
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [241,245,251], textColor: 60, halign: 'center' },
        margin: { left: 20, right: 20, top: 40 },
        didDrawPage: (data) => {
          const pageCount = doc.getNumberOfPages();
          const page = doc.getCurrentPageInfo().pageNumber;
          doc.setFontSize(9);
          doc.text(`Page ${page} of ${pageCount}`, doc.internal.pageSize.width - 60, doc.internal.pageSize.height - 10, { align: 'right' });
        }
      });
      doc.save(`ipd_discharged_${getTodayYMD()}.pdf`);
    } catch (err) {
      console.error('PDF export failed', err);
      Swal.fire('Export failed', 'Could not generate PDF in browser. See console.', 'error');
    }
  };

  // ---------- columns ----------
  const viewButtonColumn = {
    name: 'View',
    width: '90px',
    cell: (r) => (<button className="btn btn-sm btn-outline-primary" onClick={(e)=>{e.stopPropagation(); openPatientCard(r);}}>View</button>)
  };

  const commonColumns = [
    { name: 'SL', selector: (r,i)=>i+1, width: '60px' },
    { name: 'IPD No', selector: r=>`000${r.ipd_id ?? r.id ?? ''}`, sortable: true, width: '120px' },
    { name: 'UHID', selector: r=> (r.ipd_remarks==="Moved from OPD"?r.opd_uhid_move:r.ipd_uhid) || '', width: '140px' },
    { name: 'Patient Name', selector: r=>`${r.ipd_patient_title||''} ${r.ipd_patient_name||''}`, cell: r=>(<div>{r.ipd_patient_name}<br/><small className="text-muted">{r.ipd_patient_mobile || r.ipd_cont_number}</small></div>), wrap:true, width:'220px' }
  ];

  const dischargedColumns = [
    ...commonColumns,
    { name: 'Discharge Date', selector: r => r.ipd_discharge_date || r.discharge_datetime || '', sortable: true, cell: r => formatDateTime12(r.ipd_discharge_date || r.discharge_datetime), width: '180px' },
   
    // Broker columns
    { name: 'Broker', selector: r => getBrokerNameForRow(r), sortable: true, width: '200px' },
    { name: 'Broker Comm', selector: r => getBrokerNumbers(r).commission.toFixed(2), right: true, width: '120px', cell: r => <span style={{fontWeight:600}}>{`₹${getBrokerNumbers(r).commission.toFixed(2)}`}</span> },
    { name: 'Broker Paid', selector: r => getBrokerNumbers(r).paid.toFixed(2), right: true, width: '110px', cell: r => <span>₹{getBrokerNumbers(r).paid.toFixed(2)}</span> },
    { name: 'Broker Due', selector: r => getBrokerNumbers(r).due.toFixed(2), right: true, width: '110px', cell: r => <span className={getBrokerNumbers(r).due>0?"text-danger fw-bold":""}>₹{getBrokerNumbers(r).due.toFixed(2)}</span> },
    { name: 'Broker Action', width: '110px', cell: (r)=>{ const b = getBrokerNumbers(r).due; return (<button className="btn btn-sm btn-outline-primary" onClick={(e)=>{e.stopPropagation(); openBrokerPayModal(r);}} disabled={b<=0.01}>{b<=0.01? "Paid":"Pay"}</button>); }, ignoreRowClick:true, allowOverflow:true, button:true },

    { name: 'IPD Bill', selector: r => Number(r.ipd_bill_total_final ?? r.ipd_ipd_bill_amount ?? r.ipd_bill_amount ?? 0).toFixed(2), right: true, width: '120px' },
    { name: 'Paid (Cash)', selector: r => Number(r.ipd_paid_cash ?? 0).toFixed(2), right: true, width: '110px' },
    { name: 'Paid (Card)', selector: r => Number(r.ipd_paid_card ?? 0).toFixed(2), right: true, width: '110px' },
    { name: 'Paid (Online)', selector: r => Number(r.ipd_paid_online ?? 0).toFixed(2), right: true, width: '110px' },
    { name: 'Due', selector: r => Number(r.ipd_bill_total_due ?? r.ipd_due ?? r.due ?? 0).toFixed(2), right: true, width: '110px' },
    { name: 'User', selector: r => r.last_used || r.user_display || r.fname || '', width: '160px' }
  ];

  const columns = [viewButtonColumn, ...dischargedColumns];

  // ---------- patient card (small view) ----------
  const openPatientCard = async (row) => {
    if (!row) return;
    const esc = s => s === null || typeof s === 'undefined' ? '' : String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const uhid = row.ipd_uhid || row.opd_uhid_move || row.opd_uhid || '';
    const wardName = row.ipd_ward_name || row.ward_name || row.ipd_ward || 'N/A';
    const bedName  = row.ipd_bed_name  || row.bed_name || row.ipd_bed || 'N/A';
    const regDate = formatDateTime12(row.ipd_datetime);
    const discharge = formatDateTime12(row.discharge_datetime);
    const patientHtml = `
      <div style="text-align:left">
        <p><strong>UHID:</strong> ${esc(uhid) || 'N/A'}</p>
        <p><strong>IPD No:</strong> ${esc(row.ipd_id ?? row.id ?? 'N/A')}</p>
        <p><strong>Name:</strong> ${esc(row.ipd_patient_name ?? 'N/A')}</p>
        <p><strong>Phone:</strong> ${esc(row.ipd_patient_mobile ?? row.ipd_cont_number ?? 'N/A')}</p>
        <p><strong>Ward:</strong> ${esc(wardName)} &nbsp; <strong>Bed:</strong> ${esc(bedName)}</p>
        <p><strong>Admission:</strong> ${esc(regDate)}</p>
        <p><strong>Discharge:</strong> ${esc(discharge)}</p>
        <p><strong>Reg Charges:</strong> ${Number(row.ipd_reg_charges ?? 0).toFixed(2)}</p>
        <p><strong>Advance:</strong> ${Number(row.ipd_advance ?? 0).toFixed(2)}</p>
      </div>
    `;
    try {
      const fd = new FormData();
      if (row.ipd_id) fd.append('ipd_id', String(row.ipd_id));
      if (row.ipd_uhid) fd.append('ipd_uhid', String(row.ipd_uhid));
      const itemsUrl = AppConst.BASE_URL + (AppConst.getIpdBillItemsUrl || "/api/get_ipd_bill_items");
      const res = await axios.post(itemsUrl, fd);
      let items = [];
      if (res?.data) {
        if (Array.isArray(res.data.items)) items = res.data.items;
        else if (Array.isArray(res.data.records)) items = res.data.records;
        else if (Array.isArray(res.data.data)) items = res.data.data;
      }
      let itemsHtml = `<div class="mt-2"><strong>Services:</strong><div>No services found</div></div>`;
      if (items && items.length > 0) {
        const rowsHtml = items.map(it => {
          const svc = it.ipd_bill_item_service ?? it.service ?? it.name ?? it.item_name ?? 'Unknown service';
          const qty = Number(it.ipd_bill_item_qty ?? it.qty ?? it.quantity ?? 0);
          const charge = Number(it.ipd_bill_item_charge ?? it.charge ?? it.price ?? 0);
          const dis = Number(it.ipd_bill_item_dis ?? it.discount ?? 0);
          const finalAmt = Number(it.ipd_bill_item_final ?? it.final ?? it.amount ?? 0);
          return `<tr><td style="padding:6px 10px;border-bottom:1px solid #eee">${svc}</td><td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:center">${qty}</td><td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:right">${charge.toFixed(2)}</td><td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:right">${dis.toFixed(2)}</td><td style="padding:6px 10px;border-bottom:1px solid #eee;text-align:right">${finalAmt.toFixed(2)}</td></tr>`;
        }).join('');
        itemsHtml = `
          <div class="mt-2" style="width:100%;overflow:auto">
            <strong>Services:</strong>
            <table style="width:100%;border-collapse:collapse;margin-top:6px">
              <thead>
                <tr style="background:#f7f9fc;">
                  <th style="text-align:left;padding:8px 10px;border-bottom:1px solid #ddd">Service</th>
                  <th style="text-align:center;padding:8px 10px;border-bottom:1px solid #ddd;width:80px">Qty</th>
                  <th style="text-align:right;padding:8px 10px;border-bottom:1px solid #ddd;width:110px">Charge</th>
                  <th style="text-align:right;padding:8px 10px;border-bottom:1px solid #ddd;width:100px">Disc</th>
                  <th style="text-align:right;padding:8px 10px;border-bottom:1px solid #ddd;width:110px">Final</th>
                </tr>
              </thead>
              <tbody>${rowsHtml}</tbody>
            </table>
          </div>`;
      }
      const finalHtml = `<div>${patientHtml}${itemsHtml}</div>`;
      Swal.fire({ title: `Patient: ${row.ipd_patient_name || ''}`, html: finalHtml, width: 900, showCloseButton: true, confirmButtonText: 'Close' });
    } catch (err) {
      console.warn('openPatientCard failed', err);
      Swal.fire({ title: `Patient: ${row.ipd_patient_name || ''}`, html: patientHtml + `<div class="mt-2 text-danger">Could not load services</div>`, width: 900, showCloseButton: true, confirmButtonText: 'Close' });
    }
  };

  // ---------- UI ----------
  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2">
              <div className="col-md-3">
                <label>Search</label>
                <input name="search" value={filters.search} onChange={handleFilterChange} className="form-control" placeholder="UHID / Name / Mobile"/>
              </div>
              <div className="col-md-2"><label>Discharge From</label><input type="date" name="dischargeFrom" value={filters.dischargeFrom} onChange={handleFilterChange} className="form-control" /></div>
              <div className="col-md-1"><label>Time</label><input type="time" name="dischargeFromTime" value={filters.dischargeFromTime} onChange={handleFilterChange} className="form-control" /></div>
              <div className="col-md-2"><label>Discharge To</label><input type="date" name="dischargeTo" value={filters.dischargeTo} onChange={handleFilterChange} className="form-control" /></div>
              <div className="col-md-1"><label>Time</label><input type="time" name="dischargeToTime" value={filters.dischargeToTime} onChange={handleFilterChange} className="form-control" /></div>

              

              <div className="col-md-3 mt-2">
                <label>Broker</label>
                <Select
                  isClearable
                  options={brokerOptions}
                  value={ brokerOptions.find(o => String(o.value) === String(filters.brokerId)) || null }
                  onChange={(sel) => {
                    if (!sel) setFilters(prev => ({ ...prev, brokerId: "", brokerSearch: "" }));
                    else setFilters(prev => ({ ...prev, brokerId: String(sel.value), brokerSearch: String(sel.label) }));
                  }}
                  onInputChange={(val, { action }) => {
                    if (action === "input-change") fetchBrokers(val);
                  }}
                  placeholder="Select broker..."
                  isLoading={loadingBrokers}
                  styles={{ menu: base => ({ ...base, zIndex: 9999 }) }}
                />
                <small className="text-muted">Select a broker to filter</small>
              </div>

            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-end mb-2">
              <button className="btn btn-outline-primary" onClick={handleExportPdf}>Export PDF</button>
            </div>

            <DataTable
              columns={columns}
              data={filteredRecords}
              pagination
              highlightOnHover
              persistTableHead
              responsive
              customStyles={{
                headCells: { style: { backgroundColor: '#f1f5fb', color:'#405189', fontSize:'15px', fontWeight:700 } },
                rows: { style: { minHeight: '56px' } },
                cells: { style: { paddingLeft: '12px', paddingRight:'12px' } }
              }}
              onRowClicked={async (row) => { openPatientCard(row); }}
            />

            <div className="text-end mt-3 text-muted">
              <strong>Total IPD Bill:</strong> ₹{totals.totalIpdBill} &nbsp;|&nbsp;
             
              <strong>Broker Commission:</strong> <span style={{fontWeight:700}}>₹{totals.totalBrokerCommission}</span> &nbsp;|&nbsp;
              <span style={{marginLeft:8}}>💵 <strong>Paid (Cash):</strong> ₹{totals.paidCash}</span> &nbsp;|&nbsp;
              <span style={{marginLeft:8}}>💳 <strong>Paid (Card):</strong> ₹{totals.paidCard}</span> &nbsp;|&nbsp;
              <span style={{marginLeft:8}}>🌐 <strong>Paid (Online):</strong> ₹{totals.paidOnline}</span> &nbsp;|&nbsp;
              <strong>Total Due:</strong> ₹{totals.totalDue} &nbsp;|&nbsp;
              <strong>Total Patients:</strong> {totals.totalPatients}
            </div>

          </div>
        </div>
      </div>

      {/* Doctor Pay Modal */}
      {payModalOpen && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.4)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form onSubmit={handlePaySubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">Pay Doctor Commission (IPD)</h5>
                  <button type="button" className="btn-close" onClick={closePayModal}></button>
                </div>
                <div className="modal-body">
                  {payRow && (
                    <>
                      <p className="mb-2"><strong>Doctor:</strong> {payRow.ipd_bill_refered_doctor_name || payRow.ipd_refer_doctor_name || payRow.consultant_name || '-'}</p>
                      <p className="mb-2"><strong>Patient:</strong> {payRow.ipd_patient_name} ({payRow.ipd_uhid || ''})</p>
                      <p className="mb-2">
                        <strong>Commission:</strong> ₹{getDoctorNumbers(payRow).commission.toFixed(2)} &nbsp;|&nbsp;
                        <strong>Paid:</strong> ₹{getDoctorNumbers(payRow).paid.toFixed(2)} &nbsp;|&nbsp;
                        <strong>Due:</strong> ₹{getDoctorNumbers(payRow).due.toFixed(2)}
                      </p>

                      <div className="mb-3">
                        <label className="form-label">Pay Amount (₹)</label>
                        <input type="number" className="form-control" step="0.01" min="0" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
                      </div>

                      {payError && <div className="alert alert-danger py-2">{payError}</div>}
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light" onClick={closePayModal} disabled={payProcessing}>Close</button>
                  <button type="submit" className="btn btn-primary" disabled={payProcessing}>{payProcessing ? "Saving..." : "Save Payment"}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Broker Pay Modal */}
      {brokerPayModalOpen && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.4)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form onSubmit={handleBrokerPaySubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">Pay Broker Commission (IPD)</h5>
                  <button type="button" className="btn-close" onClick={closeBrokerPayModal}></button>
                </div>
                <div className="modal-body">
                  {brokerPayRow && (
                    <>
                      <p className="mb-2"><strong>Broker:</strong> {getBrokerNameForRow(brokerPayRow) || '-'}</p>
                      <p className="mb-2"><strong>Patient:</strong> {brokerPayRow.ipd_patient_name} ({brokerPayRow.ipd_uhid || ''})</p>
                      <p className="mb-2">
                        <strong>Commission:</strong> ₹{getBrokerNumbers(brokerPayRow).commission.toFixed(2)} &nbsp;|&nbsp;
                        <strong>Paid:</strong> ₹{getBrokerNumbers(brokerPayRow).paid.toFixed(2)} &nbsp;|&nbsp;
                        <strong>Due:</strong> ₹{getBrokerNumbers(brokerPayRow).due.toFixed(2)}
                      </p>

                      <div className="mb-3">
                        <label className="form-label">Pay Amount (₹)</label>
                        <input type="number" className="form-control" step="0.01" min="0" value={brokerPayAmount} onChange={(e) => setBrokerPayAmount(e.target.value)} />
                      </div>

                      {brokerPayError && <div className="alert alert-danger py-2">{brokerPayError}</div>}
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light" onClick={closeBrokerPayModal} disabled={brokerPayProcessing}>Close</button>
                  <button type="submit" className="btn btn-primary" disabled={brokerPayProcessing}>{brokerPayProcessing ? "Saving..." : "Save Payment"}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
